import type { Me } from "@fistos/shared/api";
import { useColorMode } from "@kobalte/core";
import { A } from "@solidjs/router";
import { HandFist, LogOut, Monitor, Moon, Settings, ShieldCheck, Smartphone, Sun } from "lucide-solid";
import { Show, type ParentProps } from "solid-js";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroupLabel,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Toaster } from "@/components/ui/sonner";
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip";
import { ApprovalPrompt, pendingApprovals } from "@/components/ApprovalPrompt";
import { api } from "@/lib/api";
import { live } from "@/lib/live";

function NavLink(props: ParentProps<{ href: string; end?: boolean }>) {
  return (
    <A
      href={props.href}
      end={props.end}
      class="text-muted-foreground hover:text-foreground flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors [&_svg]:size-4"
      activeClass="!text-foreground bg-accent"
    >
      {props.children}
    </A>
  );
}

function ThemeMenu() {
  const { colorMode, setColorMode } = useColorMode();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger as={Button<"button">} variant="ghost" size="icon-sm" aria-label="Theme">
        <Show when={colorMode() === "dark"} fallback={<Sun />}>
          <Moon />
        </Show>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent>
          <DropdownMenuItem onSelect={() => setColorMode("light")}>
            <Sun /> Light
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setColorMode("dark")}>
            <Moon /> Dark
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setColorMode("system")}>
            <Monitor /> System
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}

export function Shell(props: ParentProps<{ user: Me; onSignedOut: () => void }>) {
  const signOut = async () => {
    await api.logout().catch(() => {});
    props.onSignedOut();
  };
  return (
    <div class="min-h-screen">
      <header class="bg-background/85 sticky top-0 z-40 border-b backdrop-blur">
        <div class="mx-auto flex h-14 max-w-6xl items-center gap-2 px-4 sm:gap-4">
          <A href="/" class="mr-1 flex items-center gap-2 font-semibold tracking-tight">
            <span class="bg-primary text-primary-foreground flex size-7 items-center justify-center rounded-md">
              <HandFist class="size-4" />
            </span>
            <span class="hidden sm:inline">FistOS</span>
          </A>
          <nav class="flex items-center gap-0.5">
            <NavLink href="/" end>
              <Smartphone />
              <span class="hidden sm:inline">Devices</span>
            </NavLink>
            <NavLink href="/approvals">
              <ShieldCheck />
              <span class="hidden sm:inline">Approvals</span>
              <Show when={pendingApprovals().length}>
                <Badge class="bg-warning text-black h-4.5 min-w-4.5 rounded-full px-1 text-[11px]">
                  {pendingApprovals().length}
                </Badge>
              </Show>
            </NavLink>
            <NavLink href="/settings">
              <Settings />
              <span class="hidden sm:inline">Settings</span>
            </NavLink>
          </nav>
          <div class="ml-auto flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger as="span" class="flex size-8 items-center justify-center">
                <span
                  class={`size-2 rounded-full ${live.connected ? "bg-success" : "bg-destructive animate-pulse"}`}
                  aria-label={live.connected ? "Live updates connected" : "Reconnecting"}
                />
              </TooltipTrigger>
              <TooltipPortal>
                <TooltipContent>{live.connected ? "Live updates connected" : "Reconnecting…"}</TooltipContent>
              </TooltipPortal>
            </Tooltip>
            <ThemeMenu />
            <DropdownMenu>
              <DropdownMenuTrigger
                as={Button<"button">}
                variant="ghost"
                size="icon-sm"
                class="rounded-full"
                aria-label="Account"
              >
                <Show
                  when={props.user.avatarUrl}
                  fallback={
                    <span class="bg-muted flex size-7 items-center justify-center rounded-full text-xs">
                      {props.user.login[0]}
                    </span>
                  }
                >
                  <img src={props.user.avatarUrl} alt="" class="size-7 rounded-full" />
                </Show>
              </DropdownMenuTrigger>
              <DropdownMenuPortal>
                <DropdownMenuContent class="min-w-44">
                  <DropdownMenuGroupLabel class="text-muted-foreground font-normal">
                    Signed in as <span class="text-foreground font-medium">{props.user.login}</span>
                  </DropdownMenuGroupLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={signOut}>
                    <LogOut /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenuPortal>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <main class="mx-auto max-w-6xl px-4 py-6">{props.children}</main>
      <ApprovalPrompt />
      <Toaster position="top-right" />
    </div>
  );
}
